/**
 * Per-part "label printed" status for the harvest label step.
 *
 * The step is complete only when every storable part has been printed —
 * individually or via "Print all labels". Browsers can't tell whether the
 * user clicked Print or Cancel, and a resolved QZ Tray print only means the
 * OS queue accepted the job, so both browser and Zebra prints count only after
 * the technician confirms. Parts already printed are never asked about again
 * and are never un-marked.
 */

/**
 * Ids from a print batch that still need the "Did it print?" question:
 * those not yet marked printed. Empty means print without asking.
 */
export function idsNeedingConfirmation(
  batch: readonly string[],
  printed: readonly string[],
): string[] {
  const set = new Set(printed);
  return batch.filter((id, i) => !set.has(id) && batch.indexOf(id) === i);
}

/** Adds ids to the printed list (deduplicated, order kept). */
export function addPrinted(prev: readonly string[], ids: readonly string[]): string[] {
  const out = [...prev];
  for (const id of ids) if (!out.includes(id)) out.push(id);
  return out;
}

/** True only when there is at least one part and every part is printed. */
export function allLabelsPrinted(partIds: readonly string[], printed: readonly string[]): boolean {
  if (partIds.length === 0) return false;
  const set = new Set(printed);
  return partIds.every((id) => set.has(id));
}

/**
 * Restores the printed list from saved workflow state. Work orders saved
 * before per-part tracking only stored `labelsPrinted: true` after a
 * "Print all labels"; those count as all parts printed.
 */
export function hydratePrinted(
  saved: { printedPartIds?: string[]; labelsPrinted?: boolean } | null | undefined,
  partIds: readonly string[],
): string[] {
  if (saved?.printedPartIds) return [...saved.printedPartIds];
  if (saved?.labelsPrinted) return [...partIds];
  return [];
}

const nextFrame = () =>
  new Promise<void>((resolve) => {
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(() => resolve());
    else setTimeout(resolve, 16);
  });

/**
 * Opens the browser print dialog once open menus have closed (their exit
 * animation finished and the next frames have painted), and resolves after the
 * dialog is closed. It does NOT say whether anything was printed.
 */
export async function printAfterMenuCloses(
  win: Pick<Window, "print" | "addEventListener" | "removeEventListener"> = window,
  menuCloseMs = 250,
): Promise<void> {
  await new Promise((r) => setTimeout(r, menuCloseMs));
  await nextFrame();
  await nextFrame();
  await new Promise<void>((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      win.removeEventListener("afterprint", finish);
      resolve();
    };
    win.addEventListener("afterprint", finish);
    try {
      win.print();
    } catch {
      finish();
      return;
    }
    // Some browsers open the dialog without blocking or never fire afterprint.
    setTimeout(finish, 1000);
  });
}
